import { type ExceptionsStatusCodes, Exceptions } from "../../exceptions";
import { type AppLocale } from "../../locales";

export enum ApiMethod {
  GET = "GET",
  POST = "POST",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

type OptionalReturn<T> = T extends undefined ? void : T;

const defaultResponse = new Response(null, {
  status: 500,
});

type FetchApiParams = {
  method: ApiMethod;
  url: string;
  headers?: Record<string, string>;
  data?: unknown;
  getAccessToken?: (() => Promise<string | undefined>) | undefined;
  locale?: AppLocale;
  origin?: string;
  signal?: AbortSignal;
  cache?: RequestCache;
};

let getInternalAccessToken: (() => Promise<string | undefined>) | undefined = undefined;

const fetchApi = async <T = undefined>({
  method,
  url,
  headers = {},
  data,
  getAccessToken,
  locale,
  origin,
  signal,
  cache,
}: FetchApiParams): Promise<OptionalReturn<T>> => {
  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Origin: origin ?? "",
  };

  // Access token must be obtained during the fetch call because obtaining it during
  // API creation leads to an inifite loop of React re-renders due to a bug in Auth0
  // https://community.auth0.com/t/using-auth0-auth0-react-and-getaccesstokensilently-in-useeffect-leads-to-infinite-loop/104855

  const accessToken = await getAccessToken?.();
  const internalAccessToken = await getInternalAccessToken?.();

  if (accessToken) defaultHeaders["Authorization"] = `Bearer ${accessToken}`;
  else if (internalAccessToken) defaultHeaders["Authorization"] = `Bearer ${internalAccessToken}`;

  if (locale) defaultHeaders["Content-Language"] = locale;


  const response = await fetch(url, {
    method: method,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
    body: data === undefined ? null : JSON.stringify(data),
    signal,
    ...(cache !== undefined && { cache }),
  });

  const responseText = await response.text();

  // Prefer a status-specific exception when available, otherwise throw a 500 for failed responses.
  const ErrorCtor =
    Exceptions[response.status as ExceptionsStatusCodes] ??
    (response.ok ? undefined : Exceptions[500]);
  if (ErrorCtor) {
    const err = new ErrorCtor({
      message: `Error ${response.status} for ${url}`,
      response: {
        ...response,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        redirected: response.redirected,
        type: response.type,
        url: response.url,
        bodyUsed: response.bodyUsed,
      },
      stack: `httpService.fetchApi:${url} status=${response.status}`,
    });
    err.responseBody = responseText;
    throw err;
  }

  if (responseText) return JSON.parse(responseText) as OptionalReturn<T>;
  return undefined as OptionalReturn<T>;
};

export const cmsService = (configParams: { locale: AppLocale; cmsLinkWithEnv: string }) => {
  const getAll = async <T>(storiesPrefixes: string[], signal: AbortSignal): Promise<Record<string, T>> => {
    const results = await Promise.all(
      storiesPrefixes.map(async prefix => {
        const url = `${configParams.cmsLinkWithEnv}/cms-${prefix}-${configParams.locale}.json`;
        try {
          const response = await fetch(url, { cache: "no-cache", signal });
          if (!response.ok) {
            throw new Exceptions[500]({
              message: `CMS fetch failed for prefix "${prefix}" (status ${response.status})`,
              response,
              stack: `cmsService.getAll:${url}`,
            });
          }
          return (await response.json()) as Record<string, T>;
        } catch (e) {
          if (e instanceof Exceptions.Exception) throw e;
          throw new Exceptions[500]({
            message: `CMS fetch error for prefix "${prefix}": ${e instanceof Error ? e.message : String(e)}`,
            response: defaultResponse,
            stack: `cmsService.getAll:${url}`,
          });
        }
      }),
    );

    return Object.assign({}, ...results);
  };

  return {
    getAll,
  };
};

export const httpService = (configParams: {
  getAccessToken: (() => Promise<string | undefined>) | undefined;
  locale: AppLocale;
  origin?: string;
  signal?: AbortSignal;
}) => {
  getInternalAccessToken = configParams.getAccessToken;
  return {
    fetch: <T = undefined>(params: FetchApiParams) =>
      fetchApi<T>({
        ...params,
        getAccessToken: configParams.getAccessToken,
        origin: configParams.origin,
        ...(configParams.signal === undefined ? {} : { signal: configParams.signal }),
      }),
  };
};

export * from "./apiEndpoints";
export * from "./cmsEndpoints";
export * from "./uploadFileToS3";
export * from "./types";
